import { Capacitor, registerPlugin } from "@capacitor/core";

interface NativeGoogleIdentity {
  signIn(options: { serverClientId: string }): Promise<{ credential: string }>;
  signOut(): Promise<void>;
}
let plugin: NativeGoogleIdentity | undefined;
const nativePlugin = () => plugin ??= registerPlugin<NativeGoogleIdentity>("GoogleIdentity");

export function isNativeGooglePlatform(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export function googleFailureCode(error: unknown): string {
  return error && typeof error === "object" && "code" in error && typeof error.code === "string" ? error.code : "GOOGLE_SIGN_IN_FAILED";
}

export async function signInWithNativeGoogle(clientId: string): Promise<string> {
  if (!isNativeGooglePlatform() || !/^\d+-[a-z0-9_-]+\.apps\.googleusercontent\.com$/i.test(clientId)) {
    throw Object.assign(new Error("Google sign-in is not configured"), { code: "GOOGLE_CONFIG_MISSING" });
  }
  const result = await nativePlugin().signIn({ serverClientId: clientId });
  // Identity claims are never trusted here: the server verifies issuer/audience/signature.
  if (typeof result.credential !== "string" || result.credential.length > 16_384
    || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(result.credential)) {
    throw Object.assign(new Error("Google returned an invalid credential"), { code: "GOOGLE_TOKEN_INVALID" });
  }
  return result.credential;
}

/** Clear selection state, not Google permissions or the user's Google account. */
export async function clearGoogleSignInState(): Promise<void> {
  let deadline: ReturnType<typeof setTimeout> | undefined;
  try {
    if (isNativeGooglePlatform()) {
      await Promise.race([
        nativePlugin().signOut(),
        new Promise<void>((resolve) => { deadline = setTimeout(resolve, 1500); }),
      ]);
    } else if (typeof window !== "undefined") {
      const identity = window as Window & { google?: { accounts?: { id?: { disableAutoSelect?: () => void } } } };
      identity.google?.accounts?.id?.disableAutoSelect?.();
    }
  } catch { /* A missing/older provider must never prevent Varnax logout. */ }
  finally { if (deadline) clearTimeout(deadline); }
}
